import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useLocations } from '@/hooks/useApi';
import {
  useApplyOptimizationRun,
  useCreateOptimizationRun,
  useForecastStatuses,
  useLlmStatus,
  useOptimizationRun,
  type ForecastMode,
  type OptimizationRunDetail,
} from '@/hooks/optimization/useOptimizationRuns';
import { useLocationPositions } from '@/hooks/optimization/useLocationPositions';
import { useLocationDepartments } from '@/hooks/optimization/useLocationDepartments';
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  Loader2,
  Sparkles,
} from 'lucide-react';
import { MultiAgentReport } from './MultiAgentReport';

interface SmartScheduleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function defaultPeriod(): { start: string; end: string } {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  const startDate = new Date(year, month, 1); // 1-е следующего
  const endDate = new Date(year, month + 1, 0); // последний день следующего
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
      d.getDate(),
    ).padStart(2, '0')}`;
  return { start: fmt(startDate), end: fmt(endDate) };
}

function formatKzt(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 }).format(n);
}

/**
 * Достаёт человекочитаемый текст из ошибки API. apiClient бросает plain-объект
 * `{ message, status }` (НЕ Error), поэтому `err instanceof Error` для него
 * ложно — нужно разобрать message напрямую. NestJS на 400 валидации может
 * прислать message массивом строк.
 */
function extractErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error && err.message) return err.message;
  if (err && typeof err === 'object' && 'message' in err) {
    const msg = (err as { message?: unknown }).message;
    if (typeof msg === 'string' && msg.trim()) return msg;
    if (Array.isArray(msg) && msg.length > 0) return msg.join('; ');
  }
  return fallback;
}

export const SmartScheduleDialog: React.FC<SmartScheduleDialogProps> = ({
  open,
  onOpenChange,
}) => {
  const navigate = useNavigate();
  const [locationId, setLocationId] = useState<string>('');
  const initialPeriod = useMemo(defaultPeriod, []);
  const [periodStart, setPeriodStart] = useState(initialPeriod.start);
  const [periodEnd, setPeriodEnd] = useState(initialPeriod.end);
  const [forecastMode, setForecastMode] = useState<ForecastMode>('expected');
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState<string[]>(
    [],
  );
  const [selectedPositionIds, setSelectedPositionIds] = useState<string[]>([]);
  // Целевой % ФОТ от выручки. Пустая строка = не передавать в солвер.
  const [targetPayrollPercent, setTargetPayrollPercent] = useState<string>('');
  // Заметка управляющего для ИИ-агентов (банкеты, события, ремонты).
  const [managerNotes, setManagerNotes] = useState<string>('');
  const [runId, setRunId] = useState<string | null>(null);
  const [errorText, setErrorText] = useState<string>('');
  const [appliedRequestId, setAppliedRequestId] = useState<string | null>(null);
  const [requestsCreated, setRequestsCreated] = useState<number>(0);

  // Сбросить state при закрытии
  useEffect(() => {
    if (!open) {
      setRunId(null);
      setErrorText('');
      setAppliedRequestId(null);
      setRequestsCreated(0);
      setForecastMode('expected');
      setSelectedDepartmentIds([]);
      setSelectedPositionIds([]);
      setTargetPayrollPercent('');
      setManagerNotes('');
    }
  }, [open]);

  // При смене организации сбрасываем подразделения и должности.
  useEffect(() => {
    setSelectedDepartmentIds([]);
    setSelectedPositionIds([]);
  }, [locationId]);

  // При смене набора подразделений сбрасываем выбор должностей — список меняется.
  useEffect(() => {
    setSelectedPositionIds([]);
  }, [selectedDepartmentIds]);

  const { data: locationsResp } = useLocations({ limit: 200 });
  const locations: Array<{ id: string; name: string }> =
    (locationsResp as { data?: Array<{ id: string; name: string }> })?.data ?? [];

  const { data: forecastStatuses } = useForecastStatuses();
  const forecastByLocation = useMemo(() => {
    const m = new Map<string, boolean>();
    for (const s of forecastStatuses ?? []) {
      m.set(s.location_id, !!s.forecast_department_id);
    }
    return m;
  }, [forecastStatuses]);
  const selectedLocationConfigured =
    !locationId || forecastByLocation.get(locationId) !== false;

  const create = useCreateOptimizationRun();
  const apply = useApplyOptimizationRun();
  const { data: llm } = useLlmStatus();
  const { data: run } = useOptimizationRun(runId, {
    enablePolling: true,
    waitForAgentsReport: !!llm?.multi_agent_available,
  });

  const handleSubmit = async () => {
    setErrorText('');
    if (!locationId) {
      setErrorText('Выберите локацию');
      return;
    }
    if (!periodStart || !periodEnd) {
      setErrorText('Укажите период');
      return;
    }
    const parsedPercent = targetPayrollPercent.trim()
      ? Number(targetPayrollPercent.replace(',', '.'))
      : null;
    if (parsedPercent !== null) {
      if (Number.isNaN(parsedPercent) || parsedPercent <= 0 || parsedPercent > 100) {
        setErrorText('Целевой % ФОТ должен быть числом 0–100');
        return;
      }
    }
    try {
      const created = await create.mutateAsync({
        location_id: locationId,
        period_start: periodStart,
        period_end: periodEnd,
        forecast_mode: forecastMode,
        department_ids:
          selectedDepartmentIds.length > 0
            ? selectedDepartmentIds
            : undefined,
        position_ids:
          selectedPositionIds.length > 0 ? selectedPositionIds : undefined,
        target_payroll_ratio:
          parsedPercent !== null ? parsedPercent / 100 : undefined,
        manager_notes: managerNotes.trim() || undefined,
      });
      setRunId(created.id);
    } catch (err) {
      setErrorText(extractErrorMessage(err, 'Ошибка запуска'));
    }
  };

  const handleApply = async () => {
    if (!run) return;
    setErrorText('');
    try {
      const res = await apply.mutateAsync(run.id);
      setAppliedRequestId(res.applied_as_request_id);
      setRequestsCreated(res.requests.length);
    } catch (err) {
      setErrorText(extractErrorMessage(err, 'Ошибка применения'));
    }
  };

  const status = run?.status;
  const view: 'form' | 'running' | 'completed' | 'failed' | 'applied' =
    appliedRequestId
      ? 'applied'
      : !runId
      ? 'form'
      : status === 'running' || status === 'pending' || !status
      ? 'running'
      : status === 'completed'
      ? 'completed'
      : 'failed';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogClose onClose={() => onOpenChange(false)} />
        <DialogHeader>
          <div>
            <DialogTitle>
              <span className="inline-flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-purple-600" />
                Сгенерировать график (Smart Schedule)
              </span>
            </DialogTitle>
            <DialogDescription>
              Алгоритм построит оптимальный график на период с учётом прогноза
              продаж, ставок ФОТ и трудового кодекса.
            </DialogDescription>
          </div>
        </DialogHeader>

        {view === 'form' && (
          <FormView
            locations={locations}
            forecastByLocation={forecastByLocation}
            selectedLocationConfigured={selectedLocationConfigured}
            locationId={locationId}
            setLocationId={setLocationId}
            selectedDepartmentIds={selectedDepartmentIds}
            setSelectedDepartmentIds={setSelectedDepartmentIds}
            periodStart={periodStart}
            setPeriodStart={setPeriodStart}
            periodEnd={periodEnd}
            setPeriodEnd={setPeriodEnd}
            forecastMode={forecastMode}
            setForecastMode={setForecastMode}
            selectedPositionIds={selectedPositionIds}
            setSelectedPositionIds={setSelectedPositionIds}
            targetPayrollPercent={targetPayrollPercent}
            setTargetPayrollPercent={setTargetPayrollPercent}
            managerNotes={managerNotes}
            setManagerNotes={setManagerNotes}
            errorText={errorText}
            onCancel={() => onOpenChange(false)}
            onSubmit={handleSubmit}
            isSubmitting={create.isPending}
          />
        )}

        {view === 'running' && (
          <RunningView
            run={run ?? null}
            isLoadingFirst={create.isPending && !run}
          />
        )}

        {view === 'completed' && run && (
          <CompletedView
            run={run}
            errorText={errorText}
            onCancel={() => onOpenChange(false)}
            onApply={handleApply}
            isApplying={apply.isPending}
          />
        )}

        {view === 'failed' && run && (
          <FailedView
            run={run}
            onClose={() => {
              setRunId(null);
            }}
          />
        )}

        {view === 'applied' && appliedRequestId && (
          <AppliedView
            requestsCount={requestsCreated}
            onClose={() => onOpenChange(false)}
            onGotoRequests={() => {
              onOpenChange(false);
              navigate('/schedule-change-requests');
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

// =====================================================
// Sub-views
// =====================================================

const FormView: React.FC<{
  locations: Array<{ id: string; name: string }>;
  forecastByLocation: Map<string, boolean>;
  selectedLocationConfigured: boolean;
  locationId: string;
  setLocationId: (v: string) => void;
  selectedDepartmentIds: string[];
  setSelectedDepartmentIds: (v: string[]) => void;
  periodStart: string;
  setPeriodStart: (v: string) => void;
  periodEnd: string;
  setPeriodEnd: (v: string) => void;
  forecastMode: ForecastMode;
  setForecastMode: (v: ForecastMode) => void;
  selectedPositionIds: string[];
  setSelectedPositionIds: (v: string[]) => void;
  targetPayrollPercent: string;
  setTargetPayrollPercent: (v: string) => void;
  managerNotes: string;
  setManagerNotes: (v: string) => void;
  errorText: string;
  onCancel: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
}> = ({
  locations,
  forecastByLocation,
  selectedLocationConfigured,
  locationId,
  setLocationId,
  selectedDepartmentIds,
  setSelectedDepartmentIds,
  periodStart,
  setPeriodStart,
  periodEnd,
  setPeriodEnd,
  forecastMode,
  setForecastMode,
  selectedPositionIds,
  setSelectedPositionIds,
  targetPayrollPercent,
  setTargetPayrollPercent,
  managerNotes,
  setManagerNotes,
  errorText,
  onCancel,
  onSubmit,
  isSubmitting,
}) => (
  <>
    <div className="space-y-4">
      <div>
        <Label htmlFor="ss-location">Организация</Label>
        <Select value={locationId} onValueChange={setLocationId}>
          <SelectTrigger id="ss-location">
            <SelectValue placeholder="Выберите организацию" />
          </SelectTrigger>
          <SelectContent>
            {locations.map((l) => {
              const configured = forecastByLocation.get(l.id) !== false;
              return (
                <SelectItem key={l.id} value={l.id}>
                  <span className="inline-flex items-center gap-2">
                    {!configured && (
                      <AlertTriangle
                        className="h-3.5 w-3.5 text-amber-500"
                        aria-label="Не настроен forecast"
                      />
                    )}
                    {l.name}
                  </span>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
        {!selectedLocationConfigured && locationId && (
          <Alert className="mt-2 border-amber-300 bg-amber-50">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800">
              У этой организации не настроен forecast department. Заполните
              поле «Smart Schedule (прогноз продаж)» в карточке организации
              (раздел «Организации») — без него сгенерировать график нельзя.
            </AlertDescription>
          </Alert>
        )}
      </div>

      <DepartmentPicker
        locationId={locationId}
        selectedDepartmentIds={selectedDepartmentIds}
        setSelectedDepartmentIds={setSelectedDepartmentIds}
      />

      <PositionPicker
        locationId={locationId}
        departmentIds={selectedDepartmentIds}
        selectedPositionIds={selectedPositionIds}
        setSelectedPositionIds={setSelectedPositionIds}
      />

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="ss-start">Начало периода</Label>
          <Input
            id="ss-start"
            type="date"
            value={periodStart}
            onChange={(e) => setPeriodStart(e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="ss-end">Конец периода</Label>
          <Input
            id="ss-end"
            type="date"
            value={periodEnd}
            onChange={(e) => setPeriodEnd(e.target.value)}
          />
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        Период должен быть в пределах одного календарного месяца. Максимум 60
        дней.
      </p>

      <div>
        <Label>Режим прогноза</Label>
        <div className="grid grid-cols-3 gap-2 mt-1.5">
          {(
            [
              {
                value: 'expected' as ForecastMode,
                label: 'Ожидаемый',
                hint: 'predicted_sales',
              },
              {
                value: 'conservative' as ForecastMode,
                label: 'Осторожный',
                hint: 'нижняя граница',
              },
              {
                value: 'optimistic' as ForecastMode,
                label: 'Оптимистичный',
                hint: 'верхняя граница',
              },
            ] as const
          ).map((o) => {
            const active = forecastMode === o.value;
            return (
              <button
                type="button"
                key={o.value}
                onClick={() => setForecastMode(o.value)}
                className={`border rounded-md px-3 py-2 text-left text-sm transition ${
                  active
                    ? 'border-purple-500 bg-purple-50 ring-1 ring-purple-300'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <div className={`font-medium ${active ? 'text-purple-900' : ''}`}>
                  {o.label}
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {o.hint}
                </div>
              </button>
            );
          })}
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Осторожный режим даёт меньше людей в пик (минимизирует риск
          переплаты), оптимистичный — больше (под рост продаж).
        </p>
      </div>

      <div>
        <Label htmlFor="target-payroll-pct">
          Целевой % ФОТ к выручке{' '}
          <span className="text-muted-foreground text-xs font-normal">
            (опционально)
          </span>
        </Label>
        <div className="flex items-center gap-2 mt-1.5">
          <input
            id="target-payroll-pct"
            type="number"
            min={0}
            max={100}
            step={0.5}
            inputMode="decimal"
            placeholder="например, 25"
            value={targetPayrollPercent}
            onChange={(e) => setTargetPayrollPercent(e.target.value)}
            className="h-9 w-32 rounded-md border border-input bg-background px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
          <span className="text-sm text-muted-foreground">%</span>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Если задан — солвер будет активно держать total ФОТ ≤ %×выручка
          (soft constraint, превышение штрафуется). Оставьте пустым, чтобы
          просто минимизировать ФОТ без целевого порога.
        </p>
      </div>

      <div>
        <Label htmlFor="manager-notes">
          Заметка для ИИ-анализа{' '}
          <span className="text-muted-foreground text-xs font-normal">
            (опционально)
          </span>
        </Label>
        <textarea
          id="manager-notes"
          rows={2}
          maxLength={2000}
          placeholder="например: в субботу банкет на 40 человек; на следующей неделе городской фестиваль"
          value={managerNotes}
          onChange={(e) => setManagerNotes(e.target.value)}
          className="mt-1.5 w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-y"
        />
        <p className="text-xs text-muted-foreground mt-1">
          События периода, которых нет в данных (банкеты, фестивали,
          ремонты), — ИИ-агенты учтут их в рекомендациях.
        </p>
      </div>

      {errorText && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{errorText}</AlertDescription>
        </Alert>
      )}
    </div>

    <DialogFooter>
      <Button variant="outline" onClick={onCancel} disabled={isSubmitting}>
        Отмена
      </Button>
      <Button
        onClick={onSubmit}
        disabled={isSubmitting || !selectedLocationConfigured}
      >
        {isSubmitting ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Запуск...
          </>
        ) : (
          <>
            <Sparkles className="h-4 w-4 mr-2" />
            Запустить
          </>
        )}
      </Button>
    </DialogFooter>
  </>
);

const DepartmentPicker: React.FC<{
  locationId: string;
  selectedDepartmentIds: string[];
  setSelectedDepartmentIds: (v: string[]) => void;
}> = ({ locationId, selectedDepartmentIds, setSelectedDepartmentIds }) => {
  const { departments, isLoading } = useLocationDepartments(
    locationId || null,
  );
  const [open, setOpen] = useState(false);
  const popoverRef = React.useRef<HTMLDivElement>(null);

  // Закрытие при клике вне dropdown
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const selectedSet = useMemo(
    () => new Set(selectedDepartmentIds),
    [selectedDepartmentIds],
  );

  const toggle = (id: string) => {
    if (selectedSet.has(id)) {
      setSelectedDepartmentIds(selectedDepartmentIds.filter((d) => d !== id));
    } else {
      setSelectedDepartmentIds([...selectedDepartmentIds, id]);
    }
  };

  const disabled = !locationId || isLoading;

  const triggerText = useMemo(() => {
    if (!locationId) return 'Сначала выберите организацию';
    if (isLoading) return 'Загрузка...';
    if (departments.length === 0) return 'Нет подразделений';
    if (selectedDepartmentIds.length === 0) {
      return `Все подразделения (${departments.length})`;
    }
    const names = departments
      .filter((d) => selectedSet.has(d.id))
      .map((d) => d.name);
    if (names.length <= 2) return names.join(', ');
    return `${names.slice(0, 2).join(', ')} +${names.length - 2}`;
  }, [
    locationId,
    isLoading,
    departments,
    selectedDepartmentIds,
    selectedSet,
  ]);

  return (
    <div>
      <div className="flex items-center justify-between">
        <Label htmlFor="ss-department">Подразделения</Label>
        {selectedDepartmentIds.length > 0 && (
          <button
            type="button"
            onClick={() => setSelectedDepartmentIds([])}
            className="text-xs text-purple-600 hover:underline"
          >
            Сбросить
          </button>
        )}
      </div>

      <div className="relative" ref={popoverRef}>
        <button
          id="ss-department"
          type="button"
          onClick={() => !disabled && setOpen((v) => !v)}
          disabled={disabled}
          className={`w-full flex items-center justify-between border rounded-md px-3 py-2 text-sm text-left transition ${
            disabled
              ? 'bg-gray-50 text-muted-foreground cursor-not-allowed'
              : 'bg-white hover:bg-gray-50 cursor-pointer'
          } ${open ? 'ring-1 ring-purple-300 border-purple-500' : ''}`}
        >
          <span
            className={
              !locationId || selectedDepartmentIds.length === 0
                ? 'text-muted-foreground'
                : ''
            }
          >
            {triggerText}
          </span>
          <span className="inline-flex items-center gap-2">
            {selectedDepartmentIds.length > 0 && (
              <Badge variant="secondary" className="text-[10px]">
                {selectedDepartmentIds.length}
              </Badge>
            )}
            <ChevronDown
              className={`h-4 w-4 text-muted-foreground transition-transform ${
                open ? 'rotate-180' : ''
              }`}
            />
          </span>
        </button>

        {open && departments.length > 0 && (
          <div className="absolute z-50 mt-1 w-full bg-white border rounded-md shadow-lg max-h-[240px] overflow-y-auto">
            {departments.map((d) => {
              const checked = selectedSet.has(d.id);
              return (
                <label
                  key={d.id}
                  className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() => toggle(d.id)}
                  />
                  <span className="flex-1 text-sm">{d.name}</span>
                  {d.employee_count > 0 && (
                    <span className="text-xs text-muted-foreground">
                      {d.employee_count} чел.
                    </span>
                  )}
                </label>
              );
            })}
          </div>
        )}
      </div>

      {locationId && !isLoading && departments.length === 0 && (
        <p className="text-xs text-muted-foreground mt-1">
          У организации нет подразделений с активными сотрудниками.
        </p>
      )}
      {locationId && !isLoading && departments.length > 0 && (
        <p className="text-xs text-muted-foreground mt-1">
          {selectedDepartmentIds.length === 0
            ? 'Не выбрано — берём сотрудников всех подразделений'
            : `Выбрано ${selectedDepartmentIds.length} из ${departments.length}`}
        </p>
      )}
    </div>
  );
};

const PositionPicker: React.FC<{
  locationId: string;
  departmentIds: string[];
  selectedPositionIds: string[];
  setSelectedPositionIds: (v: string[]) => void;
}> = ({
  locationId,
  departmentIds,
  selectedPositionIds,
  setSelectedPositionIds,
}) => {
  const { positions, isLoading } = useLocationPositions(
    locationId || null,
    departmentIds,
  );
  const [open, setOpen] = useState(false);
  const popoverRef = React.useRef<HTMLDivElement>(null);

  // Закрытие при клике вне dropdown
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const selectedSet = useMemo(
    () => new Set(selectedPositionIds),
    [selectedPositionIds],
  );

  const toggle = (id: string) => {
    if (selectedSet.has(id)) {
      setSelectedPositionIds(selectedPositionIds.filter((p) => p !== id));
    } else {
      setSelectedPositionIds([...selectedPositionIds, id]);
    }
  };

  const disabled = !locationId || isLoading;

  const triggerText = useMemo(() => {
    if (!locationId) return 'Сначала выберите организацию';
    if (isLoading) return 'Загрузка...';
    if (positions.length === 0) {
      return departmentIds.length > 0
        ? 'В выбранных подразделениях нет активных сотрудников'
        : 'Нет должностей';
    }
    if (selectedPositionIds.length === 0) {
      return `Все должности (${positions.length})`;
    }
    const names = positions
      .filter((p) => selectedSet.has(p.id))
      .map((p) => p.name);
    if (names.length <= 2) return names.join(', ');
    return `${names.slice(0, 2).join(', ')} +${names.length - 2}`;
  }, [
    locationId,
    isLoading,
    positions,
    departmentIds,
    selectedPositionIds,
    selectedSet,
  ]);

  return (
    <div>
      <div className="flex items-center justify-between">
        <Label htmlFor="ss-position">
          Должность{' '}
          <span className="text-xs font-normal text-muted-foreground">
            (опционально)
          </span>
        </Label>
        {selectedPositionIds.length > 0 && (
          <button
            type="button"
            onClick={() => setSelectedPositionIds([])}
            className="text-xs text-purple-600 hover:underline"
          >
            Сбросить
          </button>
        )}
      </div>

      <div className="relative" ref={popoverRef}>
        <button
          id="ss-position"
          type="button"
          onClick={() => !disabled && setOpen((v) => !v)}
          disabled={disabled}
          className={`w-full flex items-center justify-between border rounded-md px-3 py-2 text-sm text-left transition ${
            disabled
              ? 'bg-gray-50 text-muted-foreground cursor-not-allowed'
              : 'bg-white hover:bg-gray-50 cursor-pointer'
          } ${open ? 'ring-1 ring-purple-300 border-purple-500' : ''}`}
        >
          <span
            className={
              !locationId || selectedPositionIds.length === 0
                ? 'text-muted-foreground'
                : ''
            }
          >
            {triggerText}
          </span>
          <span className="inline-flex items-center gap-2">
            {selectedPositionIds.length > 0 && (
              <Badge variant="secondary" className="text-[10px]">
                {selectedPositionIds.length}
              </Badge>
            )}
            <ChevronDown
              className={`h-4 w-4 text-muted-foreground transition-transform ${
                open ? 'rotate-180' : ''
              }`}
            />
          </span>
        </button>

        {open && positions.length > 0 && (
          <div className="absolute z-50 mt-1 w-full bg-white border rounded-md shadow-lg max-h-[240px] overflow-y-auto">
            {positions.map((p) => {
              const checked = selectedSet.has(p.id);
              return (
                <label
                  key={p.id}
                  className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() => toggle(p.id)}
                  />
                  <span className="flex-1 text-sm">{p.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {p.employee_count} чел.
                  </span>
                </label>
              );
            })}
          </div>
        )}
      </div>

      {locationId && !isLoading && positions.length > 0 && (
        <p className="text-xs text-muted-foreground mt-1">
          {selectedPositionIds.length === 0
            ? `Не выбрано — оптимизируем все ${positions.length} должностей`
            : `Выбрано ${selectedPositionIds.length} из ${positions.length}`}
        </p>
      )}
      {selectedPositionIds.length > 0 && (
        <p className="text-xs text-amber-700 mt-1">
          ⚠ Apply не отменит уже существующие pending заявки на это
          подразделение — сначала отмените их вручную.
        </p>
      )}
    </div>
  );
};

const RunningView: React.FC<{
  run: OptimizationRunDetail | null;
  isLoadingFirst: boolean;
}> = ({ run, isLoadingFirst }) => (
  <div className="py-12 flex flex-col items-center text-center space-y-4">
    <Loader2 className="h-12 w-12 animate-spin text-purple-600" />
    <div>
      <p className="font-medium">
        {isLoadingFirst ? 'Создаём задачу...' : 'Solver работает'}
      </p>
      <p className="text-sm text-muted-foreground mt-1">
        Сбор данных, прогноз, оптимизация. Это может занять до минуты.
      </p>
      {run && (
        <p className="text-xs text-muted-foreground mt-3">Run ID: {run.id}</p>
      )}
    </div>
  </div>
);

const MetricBox: React.FC<{
  label: string;
  value: string;
  hint?: string;
  tone?: 'default' | 'good' | 'warn';
}> = ({ label, value, hint, tone = 'default' }) => {
  const valueColor =
    tone === 'good'
      ? 'text-green-700'
      : tone === 'warn'
      ? 'text-orange-700'
      : 'text-gray-900';
  return (
    <div className="border rounded-md p-3 bg-gray-50">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`text-lg font-semibold ${valueColor}`}>{value}</div>
      {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
};

const CompletedView: React.FC<{
  run: OptimizationRunDetail;
  errorText: string;
  onCancel: () => void;
  onApply: () => void;
  isApplying: boolean;
}> = ({ run, errorText, onCancel, onApply, isApplying }) => {
  const m = run.solution?.metrics ?? {};
  const alreadyApplied = !!run.applied_as_request_id;

  // Группируем по сотруднику для компактности
  const perEmployee = useMemo(() => {
    const assignments = run.solution?.assignments ?? [];
    const map = new Map<string, string[]>();
    for (const a of assignments) {
      const arr = map.get(a.employee_id) ?? [];
      arr.push(a.date);
      map.set(a.employee_id, arr);
    }
    return Array.from(map.entries())
      .map(([empId, dates]) => ({
        empId,
        days: dates.length,
        firstDate: [...dates].sort()[0],
      }))
      .sort((a, b) => b.days - a.days);
  }, [run.solution?.assignments]);

  const assignments = run.solution?.assignments ?? [];

  return (
    <>
      <div className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricBox
            label="ФОТ"
            value={m.total_fot ? `${formatKzt(m.total_fot)} ₸` : '—'}
            tone="default"
          />
          <MetricBox
            label="Покрытие спроса"
            value={
              m.coverage_score !== undefined
                ? `${(m.coverage_score * 100).toFixed(0)}%`
                : '—'
            }
            tone={
              m.coverage_score !== undefined && m.coverage_score >= 0.9
                ? 'good'
                : 'warn'
            }
          />
          <MetricBox
            label="Недокрытие"
            value={
              m.understaffed_hours !== undefined
                ? `${m.understaffed_hours} ч`
                : '—'
            }
            tone={m.understaffed_hours ? 'warn' : 'good'}
          />
          <MetricBox
            label="Переработки"
            value={
              m.overtime_hours !== undefined ? `${m.overtime_hours} ч` : '—'
            }
            tone={m.overtime_hours ? 'warn' : 'good'}
          />
        </div>

        <div className="text-xs text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
          <span>Solver: {run.solver_status ?? '—'}</span>
          <span>Время: {run.solve_time_ms ? `${run.solve_time_ms} мс` : '—'}</span>
          <span>Назначений: {assignments.length}</span>
          <span>Сотрудников: {perEmployee.length}</span>
        </div>

        <DefaultsAppliedBlock run={run} />

        <MultiAgentReport
          run={run}
          waitingForGeneration={
            !run.llm_agents_output?.summary &&
            Date.now() - new Date(run.created_at).getTime() < 2 * 60_000
          }
        />

        {perEmployee.length > 0 && (
          <div className="border rounded-md max-h-[280px] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Сотрудник</TableHead>
                  <TableHead className="text-right">Рабочих дней</TableHead>
                  <TableHead>Первый день</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {perEmployee.slice(0, 50).map((e) => (
                  <TableRow key={e.empId}>
                    <TableCell className="font-mono text-xs">
                      {e.empId.slice(0, 8)}...
                    </TableCell>
                    <TableCell className="text-right">{e.days}</TableCell>
                    <TableCell>{e.firstDate}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {perEmployee.length > 50 && (
              <div className="text-xs text-muted-foreground p-2 text-center">
                ... и ещё {perEmployee.length - 50}
              </div>
            )}
          </div>
        )}

        {alreadyApplied && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Этот run уже применён ранее. Заявка:{' '}
              {run.applied_as_request_id?.slice(0, 8)}...
            </AlertDescription>
          </Alert>
        )}

        {errorText && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{errorText}</AlertDescription>
          </Alert>
        )}
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onCancel} disabled={isApplying}>
          Закрыть
        </Button>
        <Button
          onClick={onApply}
          disabled={isApplying || alreadyApplied || assignments.length === 0}
        >
          {isApplying ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Применение...
            </>
          ) : (
            <>
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Применить
            </>
          )}
        </Button>
      </DialogFooter>
    </>
  );
};

const FailedView: React.FC<{
  run: OptimizationRunDetail;
  onClose: () => void;
}> = ({ run, onClose }) => (
  <div className="space-y-4">
    <Alert variant="destructive">
      <AlertCircle className="h-4 w-4" />
      <AlertTitle>Ошибка оптимизации</AlertTitle>
      <AlertDescription>
        Solver: {run.solver_status ?? 'unknown'}
        {run.notes && (
          <>
            <br />
            <span className="text-xs">{run.notes}</span>
          </>
        )}
      </AlertDescription>
    </Alert>

    <DialogFooter>
      <Button variant="outline" onClick={onClose}>
        Назад
      </Button>
    </DialogFooter>
  </div>
);

const AppliedView: React.FC<{
  requestsCount: number;
  onClose: () => void;
  onGotoRequests: () => void;
}> = ({ requestsCount, onClose, onGotoRequests }) => (
  <div className="space-y-4">
    <Alert className="border-green-300 bg-green-50">
      <CheckCircle2 className="h-5 w-5 text-green-600" />
      <AlertTitle className="text-green-800">График применён</AlertTitle>
      <AlertDescription className="text-green-700">
        Создано заявок: {requestsCount} (по одной на каждое подразделение).
        Заявки в статусе «pending» — HR должен их рассмотреть.
      </AlertDescription>
    </Alert>

    <DialogFooter>
      <Button variant="outline" onClick={onClose}>
        Закрыть
      </Button>
      <Button onClick={onGotoRequests}>Перейти к заявкам</Button>
    </DialogFooter>
  </div>
);

// =====================================================
// Auto-defaults block (shown in completed view)
// =====================================================

interface DefaultsAppliedShape {
  hourly_profile?: 'refreshed' | 'cached' | 'failed';
  hourly_profile_age_days?: number | null;
  shifts_created?: Array<{ id: string; code: string; name: string }>;
  shifts_eligibility_added?: Array<{
    shift_id: string;
    added_position_ids: string[];
  }>;
  labor_rules_created?: string[];
  calibration_created?: Array<{
    position_id: string;
    target_revenue_per_hour: number;
  }>;
  operating_hours?: {
    is_24_7: boolean;
    opening_hour: number | null;
    closing_hour: number | null;
    source: 'aqniet' | 'fallback';
  };
  notes?: string[];
}

const DefaultsAppliedBlock: React.FC<{ run: OptimizationRunDetail }> = ({
  run,
}) => {
  const snapshot = run.input_snapshot as
    | { defaults_applied?: DefaultsAppliedShape }
    | undefined;
  const d = snapshot?.defaults_applied;
  if (!d) return null;

  const items: string[] = [];
  if (d.shifts_created && d.shifts_created.length > 0) {
    items.push(`смены: ${d.shifts_created.length}`);
  }
  if (
    d.shifts_eligibility_added &&
    d.shifts_eligibility_added.length > 0
  ) {
    const totalPositions = d.shifts_eligibility_added.reduce(
      (n, s) => n + s.added_position_ids.length,
      0,
    );
    items.push(`позиции к сменам: ${totalPositions}`);
  }
  if (d.labor_rules_created && d.labor_rules_created.length > 0) {
    items.push(`трудовых правил: ${d.labor_rules_created.length}`);
  }
  if (d.calibration_created && d.calibration_created.length > 0) {
    const target = d.calibration_created[0]?.target_revenue_per_hour;
    items.push(
      `калибровка: ${d.calibration_created.length} поз. × ${formatKzt(
        target,
      )} ₸/ч`,
    );
  }
  if (d.hourly_profile === 'refreshed') {
    items.push('hourly profile обновлён');
  }

  const hasContent = items.length > 0 || (d.notes && d.notes.length > 0);
  if (!hasContent) return null;

  const oh = d.operating_hours;
  const hoursLabel = oh
    ? oh.is_24_7
      ? '24/7'
      : `${oh.opening_hour ?? '?'}:00–${oh.closing_hour ?? '?'}:00`
    : null;

  return (
    <Alert className="border-blue-200 bg-blue-50">
      <Sparkles className="h-4 w-4 text-blue-600" />
      <AlertTitle className="text-blue-900">
        Авто-настройки применены
      </AlertTitle>
      <AlertDescription className="text-blue-800 text-xs space-y-1">
        {items.length > 0 && <div>{items.join(' • ')}</div>}
        {hoursLabel && (
          <div>
            Часы работы: <span className="font-medium">{hoursLabel}</span>{' '}
            <span className="opacity-70">
              (источник: {oh!.source === 'aqniet' ? 'aqniet' : 'fallback'})
            </span>
          </div>
        )}
        {d.notes && d.notes.length > 0 && (
          <ul className="list-disc ml-4">
            {d.notes.map((n, i) => (
              <li key={i}>{n}</li>
            ))}
          </ul>
        )}
        <div className="opacity-80 pt-1">
          Переопределить можно в разделе{' '}
          <a
            className="underline hover:no-underline"
            href="/smart-schedule"
            target="_blank"
            rel="noreferrer"
          >
            Smart Schedule → Настройки
          </a>
          .
        </div>
      </AlertDescription>
    </Alert>
  );
};

export default SmartScheduleDialog;
